import React from "react";
import ReactDOM from "react-dom";
import { Provider } from "react-redux";

import * as Sentry from "@sentry/gatsby";

// Utils
import { analytics as analyticsService } from "./src/utils/analytics";
import { currentPageData } from "./src/utils/currentPageData";
import { polyfillPluralRules, preloadTranslations } from "./src/utils/intl";
import metrics from "./src/utils/metrics";

// Services
import launchDarkly from "./src/services/launchDarkly";
import castle from "./src/services/castle";

// Store
import { getStore } from "./src/store/createStore";
import { updateRoute } from "./src/store/route/actions";
import InitializeStore from "./src/components/global/InitializeStore";
import InitializeLocale from "./src/components/global/InitializeLocale";

// Animations
import { TweenMax } from "gsap";
import ScrollMagic from "scrollmagic";
import ScrollMagicGsapPlugin from "ScrollMagicGsapPlugin";

import { defineCustomElements } from "@ritual/essentials-for-react";

export const wrapRootElement = ({ element }) => {
  Sentry.init({
    dsn: process.env.GATSBY_SENTRY_DSN,
    environment: process.env.GATSBY_ACTIVE_ENV || "development",
    release: process.env.GATSBY_APP_VERSION,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        networkDetailAllowUrls: [
          window.location.origin,
          /^https:\/\/api\.ritual\.com\/api/,
          /^https:\/\/api\.seg\.ritual\.com\/.*/,
          /^https:\/\/(?:.+\.)?ritual\.engineering\/api/,
        ],
      }),
      Sentry.launchDarklyIntegration(),
    ],
    // Tracing
    tracesSampleRate:
      parseFloat(process.env.GATSBY_SENTRY_TRACE_SAMPLE_RATE) || 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [
      "localhost",
      /^https:\/\/api\.ritual\.com\/api/,
      /^https:\/\/(?:.+\.)?ritual\.engineering\/api/,
    ],
    // Session Replay
    replaysSessionSampleRate:
      parseFloat(process.env.GATSBY_SENTRY_REPLAY_SESSION_SAMPLE_RATE) || 1.0,
    replaysOnErrorSampleRate:
      parseFloat(process.env.GATSBY_SENTRY_REPLAY_ERROR_SAMPLE_RATE) || 1.0,
  });

  // Instantiating store in `wrapRootElement` handler ensures:
  //  - there is fresh store for each SSR page
  //  - it will be called only once in browser, when React mounts
  const store = getStore();

  return (
    <Sentry.ErrorBoundary>
      <InitializeStore store={store}>
        <Provider store={store}>{element}</Provider>
      </InitializeStore>
    </Sentry.ErrorBoundary>
  );
};

export const onClientEntry = async () => {
  defineCustomElements();
  // Initializes ScrollMagic with the GSAP animations plugin.
  ScrollMagicGsapPlugin(ScrollMagic, TweenMax);

  castle.initialize();

  await Promise.all([
    require(`intersection-observer`),
    // Optionally load Intl.PluralRules if not supported natively
    polyfillPluralRules(),
    // Delay rendering until translation data has been grabbed. This ensures
    // there is no flash of missing content when rehydrating from the server.
    preloadTranslations(),

    // Delay rendering until Launch Darkly has been initialized. This ensures
    // that the initial content is rendered with correct feature values instead
    // of default values, which prevents flickering.

    launchDarkly.initialize(),
  ]);
};

export const onInitialClientRender = () => {
  const userInfo = analyticsService.getStoredUserInfo();

  Sentry.setUser({
    id: userInfo.id || null,
    email: userInfo.traits.email || null,
  });

  // @axe-core/react implementation
  if (
    process.env.NODE_ENV === "development" &&
    process.env.ENABLE_AXE_CORE === "true"
  ) {
    import("@axe-core/react").then((axe) => {
      axe.default(React, ReactDOM, 1000);
    });
  }
};

export const wrapPageElement = ({ element, props }) => {
  const { locale, activeLocales } = props.pageContext;
  return (
    <InitializeLocale locale={locale} activeLocales={activeLocales}>
      {element}
    </InitializeLocale>
  );
};

export const onPreRouteUpdate = ({ location, prevLocation }) => {
  // Reset the page label on transitions.
  currentPageData.label = null;
};

export const onRouteUpdate = ({ location, prevLocation }) => {
  const pageLabel = currentPageData.label;

  getStore().dispatch(updateRoute({ location, prevLocation }));

  if (!window.analytics || typeof window.analytics.page !== "function") {
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.warn("Unable to locate analytics.js");
    }
    return;
  }

  metrics.page(pageLabel);
};
